/**
 * ActivityCard — inline HubSpot-style activity row. Collapsed by default
 * (header + 2-line body preview); clicking the chevron or header expands
 * it to show the full body + actions. Edit mode swaps the body for a
 * textarea with Save / Cancel directly on the card (no modal).
 *
 * Designed to render inside the ContactDetail Activities feed and the
 * right-rail Recent Activity card.
 */
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import {
    FileText, Phone, Calendar, Mail, MessageCircle, ChevronDown, ChevronUp,
    MoreHorizontal, Edit3, Save, X as XIcon, Loader2, History, Clock, User,
} from 'lucide-react';
import OutreachSendCard from './OutreachSendCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_VISUAL = {
    note: { Icon: FileText, className: 'text-stone-400' },
    email: { Icon: Mail, className: 'text-blue-500' },
    call: { Icon: Phone, className: 'text-emerald-500' },
    meeting: { Icon: Calendar, className: 'text-amber-500' },
    task: { Icon: FileText, className: 'text-indigo-500' },
    whatsapp: { Icon: MessageCircle, className: 'text-green-500' },
};

function formatStamp(value) {
    if (!value) return '';
    try { return new Date(value).toLocaleString(); } catch { return ''; }
}

function formatRelative(value) {
    if (!value) return '';
    const ms = Date.now() - new Date(value).getTime();
    if (Number.isNaN(ms) || ms < 0) return formatStamp(value);
    const s = Math.floor(ms / 1000);
    if (s < 60) return 'just now';
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    if (d < 7) return `${d}d ago`;
    return formatStamp(value);
}

export default function ActivityCard({
    activity,
    expanded,
    onToggle,
    onSaved,
    autoFocus = false,
}) {
    const { user } = useAuth();
    const [editing, setEditing] = useState(false);
    const [title, setTitle] = useState(activity?.title || '');
    const [description, setDescription] = useState(activity?.description || activity?.notes || '');
    const [saving, setSaving] = useState(false);
    const [showActions, setShowActions] = useState(false);
    const cardRef = useRef(null);

    useEffect(() => {
        setTitle(activity?.title || '');
        setDescription(activity?.description || activity?.notes || '');
        setEditing(false);
    }, [activity?.activity_id, activity?.id]);

    useEffect(() => {
        if (autoFocus && cardRef.current) {
            cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [autoFocus]);

    if (!activity) return null;

    // Event Outreach send tasks get a bespoke card render (Mark sent /
    // Capture response / Rework next). Regular activities fall through
    // to the generic renderer below.
    if (activity.task_kind === 'outreach_send') {
        return (
            <OutreachSendCard
                activity={activity}
                expanded={expanded}
                onToggle={onToggle}
                onChanged={(next, meta) => onSaved?.(next, meta)}
            />
        );
    }

    const typeKey = (activity.type || 'note').toLowerCase();
    const { Icon, className: typeClass } = TYPE_VISUAL[typeKey] || TYPE_VISUAL.note;

    // Use activity_id; fall back to id for backwards-compat with timeline
    // rows that didn't carry both before the backend update.
    const activityId = activity.activity_id || activity.id;

    const isAuthor = user?.user_id && activity.owner_id === user.user_id;
    const isAdmin = user?.is_admin || user?.is_super_admin;
    const canEdit = Boolean(isAuthor || isAdmin);

    const body = activity.description || activity.notes || '';
    const hasEdits = Array.isArray(activity.edit_log) && activity.edit_log.length > 0;
    const lastEdit = hasEdits ? activity.edit_log[activity.edit_log.length - 1] : null;

    const handleSave = async () => {
        if (!title.trim()) { toast.error('Title is required'); return; }
        if (!activityId) { toast.error('Cannot edit — missing activity id'); return; }
        setSaving(true);
        try {
            const token = localStorage.getItem('crm_token');
            const res = await axios.put(
                `${API}/activities/${activityId}`,
                { title: title.trim(), description: description.trim() || null },
                { headers: token ? { Authorization: `Bearer ${token}` } : {} },
            );
            toast.success('Saved');
            setEditing(false);
            onSaved?.(res.data);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(detail || 'Failed to save activity');
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        setTitle(activity.title || '');
        setDescription(activity.description || activity.notes || '');
        setEditing(false);
    };

    return (
        <div
            ref={cardRef}
            className="border border-stone-200 rounded-lg bg-white overflow-hidden"
            data-activity-id={activityId}
        >
            {/* ---- Card header (always visible) ---- */}
            <div className="flex items-start gap-3 p-3">
                <button
                    type="button"
                    onClick={onToggle}
                    className="flex-shrink-0 p-0.5 text-stone-400 hover:text-stone-700"
                    aria-label={expanded ? 'Collapse' : 'Expand'}
                >
                    {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
                <div className="w-7 h-7 rounded-full bg-stone-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Icon className={`w-3.5 h-3.5 ${typeClass}`} />
                </div>
                <button
                    type="button"
                    onClick={onToggle}
                    className="flex-1 min-w-0 text-left"
                >
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-stone-800 capitalize">{activity.type}</span>
                        {activity.owner_name && (
                            <span className="text-xs text-stone-500">by {activity.owner_name}</span>
                        )}
                        {hasEdits && (
                            <span className="text-[10px] text-stone-400 italic">· edited</span>
                        )}
                    </div>
                    <div className="flex items-center gap-1.5 mt-0.5 text-[11px] text-stone-400">
                        <Clock className="w-3 h-3" />
                        <span>{formatStamp(activity.created_at || activity.date)}</span>
                    </div>
                </button>
                {canEdit && !editing && (
                    <div className="relative flex-shrink-0">
                        <button
                            type="button"
                            onClick={() => setShowActions(s => !s)}
                            className="p-1 text-stone-400 hover:text-stone-700 hover:bg-stone-100 rounded"
                            aria-haspopup="menu"
                            aria-expanded={showActions}
                            data-action="activity.actions_menu"
                        >
                            Actions <ChevronDown className="w-3 h-3 inline-block -mt-0.5" />
                        </button>
                        {showActions && (
                            <div
                                className="absolute right-0 top-full mt-1 w-36 bg-white border border-stone-200 rounded-lg shadow-lg z-10 py-1"
                                onMouseLeave={() => setShowActions(false)}
                            >
                                <button
                                    type="button"
                                    onClick={() => { setShowActions(false); setEditing(true); if (!expanded) onToggle?.(); }}
                                    className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-stone-700 hover:bg-stone-50 text-left"
                                    data-action="activity.edit"
                                >
                                    <Edit3 className="w-3 h-3" /> Edit
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* ---- Collapsed preview: 2-line body snippet ---- */}
            {!expanded && body && (
                <button
                    type="button"
                    onClick={onToggle}
                    className="block w-full text-left px-3 pb-3 -mt-1 text-xs text-stone-500 line-clamp-2 whitespace-pre-wrap hover:text-stone-700"
                >
                    {body}
                </button>
            )}

            {/* ---- Expanded body ---- */}
            {expanded && (
                <div className="px-3 pb-3">
                    {editing ? (
                        <div className="space-y-2">
                            <input
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                className="w-full px-2 py-1.5 text-sm border border-stone-200 rounded-md focus:outline-none focus:ring-2 focus:ring-stone-900/20"
                                placeholder="Title"
                                data-testid="activity-card-title"
                            />
                            <textarea
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                rows={6}
                                className="w-full px-2 py-1.5 text-sm border border-stone-200 rounded-md focus:outline-none focus:ring-2 focus:ring-stone-900/20 resize-vertical"
                                placeholder="Add details…"
                                autoFocus
                                data-testid="activity-card-description"
                            />
                            <div className="flex items-center gap-2 pt-1">
                                <button
                                    type="button"
                                    onClick={handleSave}
                                    disabled={saving}
                                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-stone-900 text-white rounded-md hover:bg-stone-800 disabled:opacity-60"
                                    data-testid="activity-card-save"
                                >
                                    {saving
                                        ? <Loader2 className="w-3 h-3 animate-spin" />
                                        : <Save className="w-3 h-3" />}
                                    Save
                                </button>
                                <button
                                    type="button"
                                    onClick={handleCancel}
                                    disabled={saving}
                                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs text-stone-600 hover:text-stone-900"
                                >
                                    <XIcon className="w-3 h-3" /> Cancel
                                </button>
                            </div>
                        </div>
                    ) : (
                        <>
                            {body ? (
                                <p className="text-sm text-stone-700 whitespace-pre-wrap leading-relaxed">{body}</p>
                            ) : (
                                <p className="text-sm text-stone-400 italic">No details recorded.</p>
                            )}
                            {hasEdits && (
                                <div className="mt-3 pt-2 border-t border-stone-100">
                                    <div className="flex items-center gap-1.5 text-[11px] text-stone-500">
                                        <History className="w-3 h-3" />
                                        <span>
                                            Edited by <span className="font-medium text-stone-700">{lastEdit?.user_name || 'Unknown'}</span> · {formatRelative(lastEdit?.timestamp)}
                                        </span>
                                    </div>
                                    {activity.edit_log.length > 1 && (
                                        <details className="mt-2 text-[10px] text-stone-500">
                                            <summary className="cursor-pointer hover:text-stone-700">
                                                Show full edit history ({activity.edit_log.length} entries)
                                            </summary>
                                            <div className="mt-1.5 space-y-1.5 pl-3 border-l border-stone-100">
                                                {[...activity.edit_log].reverse().map((entry) => (
                                                    <div key={entry.log_id}>
                                                        <div className="text-stone-600">
                                                            <span className="font-medium">{entry.user_name || 'Unknown'}</span>
                                                            {' · '}
                                                            {formatStamp(entry.timestamp)}
                                                        </div>
                                                        {Array.isArray(entry.changes) && entry.changes.map((c, i) => (
                                                            <div key={i} className="text-stone-400 mt-0.5">
                                                                {c.field}: <span className="line-through">{c.old || '(empty)'}</span> → <span className="text-stone-600">{c.new || '(empty)'}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                ))}
                                            </div>
                                        </details>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
}
